import { Skeleton } from "@/components/ui/skeleton";
import { ChartCard } from "./chart-card";
import { cn } from "@/lib/utils";
import type { SubjectScoreRow } from "@/lib/statistics-api.service";

interface SubjectPopularityChartProps {
	/** `undefined` = lần tải đầu chưa xong. */
	rows: SubjectScoreRow[] | undefined;
}

const TITLE = "Độ phổ biến và điểm theo môn";

/** Ngưỡng coi là "mẫu nhỏ" — dưới 5%, đúng nhóm môn đuôi nêu trong pha 10. */
const SMALL_SAMPLE_SHARE = 5;

/**
 * Sắp môn theo `count` GIẢM DẦN — môn có mặt trong nhiều hồ sơ nhất lên đầu.
 * Server đã sắp sẵn đúng thứ tự này, nhưng sắp lại ở client để component
 * không phụ thuộc ngầm vào thứ tự trả về của một endpoint cụ thể.
 */
export function sortSubjectPopularity(rows: readonly SubjectScoreRow[]): SubjectScoreRow[] {
	return [...rows].sort((a, b) => b.count - a.count);
}

/**
 * Độ phổ biến (số/tỉ lệ thí sinh có môn này trong tổ hợp) theo từng môn, kèm
 * điểm TB để có ngữ cảnh.
 *
 * KHÔNG còn tách theo tiếp nhận: tập hồ sơ của cả tab đã là "đã tiếp nhận hoặc
 * đã đóng học phí", nên mọi hàng đều thuộc cùng một nhóm và một thanh chia đôi
 * sẽ luôn đầy 100%.
 *
 * Nhóm môn đuôi (Lịch sử, Văn, CNNN, Địa lí, CNCN — đều dưới 5% hồ sơ) có mẫu
 * rất nhỏ (Địa lí chỉ 88 người): số/tỉ lệ của chúng dao động mạnh theo mẫu, vẽ
 * cùng thang với Toán (5.162 người) mà không có gì báo là chưa đủ. Mỗi hàng vì
 * vậy in kèm cỡ mẫu ngay cạnh tỉ lệ, và nhãn "mẫu nhỏ" khi share < 5%.
 *
 * KHÔNG xếp hạng "chất lượng" môn từ điểm hay tỉ lệ tiếp nhận — chỉ trình bày
 * theo thứ tự phổ biến, giữ đúng ràng buộc đặt tên/không phán xét của pha 09.
 */
export function SubjectPopularityChart({ rows }: SubjectPopularityChartProps) {
	if (rows === undefined) {
		return (
			<ChartCard title={TITLE} filename="do-pho-bien-va-diem-theo-mon">
				<div className="space-y-3">
					{Array.from({ length: 6 }).map((_, i) => (
						<Skeleton key={i} className="h-8 w-full" />
					))}
				</div>
			</ChartCard>
		);
	}

	if (rows.length === 0) {
		return (
			<ChartCard title={TITLE} filename="do-pho-bien-va-diem-theo-mon">
				<p className="py-8 text-center text-sm text-muted-foreground">
					Chưa có dữ liệu điểm theo môn trong phạm vi đang lọc.
				</p>
			</ChartCard>
		);
	}

	const sorted = sortSubjectPopularity(rows);
	const maxCount = Math.max(...sorted.map((row) => row.count), 1);

	return (
		<ChartCard
			title={TITLE}
			filename="do-pho-bien-va-diem-theo-mon"
			description="Sắp theo số thí sinh giảm dần — không phải xếp hạng chất lượng môn"
		>
			<div className="space-y-2.5">
				{sorted.map((row) => {
					const barPct = (row.count / maxCount) * 100;
					const smallSample = row.share < SMALL_SAMPLE_SHARE;

					return (
						<div key={row.subject} className="flex items-center gap-3">
							<span className="w-24 flex-shrink-0 truncate text-sm md:w-28" title={row.subject}>
								{row.subject}
							</span>
							<div className="relative h-5 flex-1 rounded bg-secondary">
								{/* Thanh dài theo `count` — cỡ mẫu so với môn đông nhất. */}
								<div
									className="absolute inset-y-0 left-0 rounded bg-primary"
									style={{ width: `${barPct}%` }}
									title={`Có môn này: ${row.count.toLocaleString("vi-VN")} thí sinh (${row.share.toFixed(1)}%)`}
								/>
							</div>
							<span className="w-64 flex-shrink-0 text-right text-xs text-muted-foreground md:text-sm">
								<span className={cn("font-semibold text-foreground", smallSample && "font-normal")}>
									{row.count.toLocaleString("vi-VN")}
								</span>{" "}
								SV ({row.share.toFixed(1)}%) · TB {row.avgScore.toFixed(2)}
								{smallSample && <span className="ml-1 italic">· mẫu nhỏ</span>}
							</span>
						</div>
					);
				})}
			</div>

			<p className="mt-3 text-xs text-muted-foreground">
				"Mẫu nhỏ" đánh dấu các môn dưới 5% hồ sơ — điểm TB của nhóm này dao động mạnh theo mẫu, đừng
				so ngang với Toán hay Vật lí như thể cùng cỡ mẫu.
			</p>
		</ChartCard>
	);
}
